import React from 'react';
import matchMedia from 'matchmedia';
import theme from 'app/theme';
import { debounce } from 'lodash';

const MOBILE_MAX = theme.breakpointNumeric.mobile;
const TABLET_PORTRAIT_MIN = theme.breakpointNumeric.mobile + 1;
const TABLET_PORTRAIT_MAX = theme.breakpointNumeric.tabletPortrait;
const TABLET_LANDSCAPE_MIN = theme.breakpointNumeric.tabletPortrait + 1;
const TABLET_LANDSCAPE_MAX = theme.breakpointNumeric.tabletLandscape;
const DESKTOP_MIN = theme.breakpointNumeric.tabletLandscape + 1;
const DESKTOP_LARGE_MIN = theme.breakpointNumeric.desktop + 1;

type Matches = {
  desktop: boolean,
  desktopLarge: boolean,
  mobile: boolean,
  tabletPortrait: boolean,
  tabletLandscape: boolean,
};

type Props = {
  children: (matches: Matches) => void,
};

type State = {
  matches: Matches,
};

/**
 * Builds a media query from the min/max values given
 * @param {number} min The minimum value in pixels for the media query
 * @param {number} max The maximuim value in pixels for the media query
 */
function buildQuery(min: Number, max?: Number) {
  if (!max) {
    return `(min-width: ${min}px)`;
  }
  if (!min) {
    return `(max-width: ${max}px)`;
  }

  return `(min-width: ${min}px) and (max-width: ${max}px)`;
}

// set up a single match media instance for all responsive multi's to listen on
// this'll reduce the number created
const mobile = matchMedia(buildQuery(undefined, MOBILE_MAX));
const tabletPortrait = matchMedia(buildQuery(TABLET_PORTRAIT_MIN, TABLET_PORTRAIT_MAX));
const tabletLandscape = matchMedia(buildQuery(TABLET_LANDSCAPE_MIN, TABLET_LANDSCAPE_MAX));
const desktop = matchMedia(buildQuery(DESKTOP_MIN));
const desktopLarge = matchMedia(buildQuery(DESKTOP_LARGE_MIN));

// Helper class to keep track of the 'nextMatch' and ensure that
// only one version of the query listeners needs to exist
class ResponsiveHelper {
  nextMatch: Matches = {};

  listeners: Set = new Set();

  // all our listeners for this component
  desktopListener = mql => this.updateMatches({ desktop: mql.matches });

  desktopLargeListener = mql => this.updateMatches({ desktopLarge: mql.matches });

  mobileListener = mql => this.updateMatches({ mobile: mql.matches });

  tabletPotraitListener = mql => this.updateMatches({ tabletPortrait: mql.matches });

  tabletLandscapeListener = mql => this.updateMatches({ tabletLandscape: mql.matches });

  constructor() {
    // start listening on changes to the media query stuff
    desktop.addListener(this.desktopListener);
    desktopLarge.addListener(this.desktopLargeListener);
    mobile.addListener(this.mobileListener);
    tabletPortrait.addListener(this.tabletPotraitListener);
    tabletLandscape.addListener(this.tabletLandscapeListener);

    // Initialise the values the first time
    this.desktopListener(desktop);
    this.desktopLargeListener(desktopLarge);
    this.mobileListener(mobile);
    this.tabletPotraitListener(tabletPortrait);
    this.tabletLandscapeListener(tabletLandscape);
  }

  // debounced version as what often happens is one breakpoint turns on
  // another turns off. So we're batching them together with a debounce
  setMatches = debounce(() => {
    // let our listeners know we've got new values
    // seeing as nextMatch gets updated outside of the debounce
    // this should always be the latest by now
    const savedNextMatch = this.nextMatch;
    this.listeners.forEach(callback => callback(savedNextMatch));
  }, 1);

  updateMatches = (newVal) => {
    // update our matches with the next value...
    this.nextMatch = {
      ...this.nextMatch,
      ...newVal,
    };
    this.setMatches();
  }

  registerListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }
}

const responsiveHelper = new ResponsiveHelper();


class ResponsiveMulti extends React.Component {
  props: Props;

  state: State = {
    matches: responsiveHelper.nextMatch,
  };

  componentDidMount() {
    responsiveHelper.registerListener(this.updateMatches);
  }

  componentWillUnmount() {
    responsiveHelper.removeListener(this.updateMatches);
  }

  updateMatches = (nextMatches: Matches) => {
    this.setState({ matches: nextMatches });
  }

  render() {
    const {
      children,
    } = this.props;

    if (typeof children === 'function') {
      return children(this.state.matches);
    }
    return undefined;
  }
}


export default ResponsiveMulti;
